import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectTheme } from '@store/ThemeSlice';
import {
  defaultColors,
  lightColors,
  lightSemanticColors,
  baseTypography,
  baseShape,
  baseShadows,
  lightModeComponents,
} from '@theme/index';

/**
 * Global theme wrapper - always uses light mode.
 * Dark mode is handled by AdminThemeWrapper/SysadminThemeWrapper for admin sections only.
 * Formulaire and Vote pages should always be in light mode.
 */
function ThemeWrapper({ children }: Readonly<{ children: React.ReactNode }>) {
  const themeConfig = useSelector(selectTheme);

  const primaryColor = themeConfig?.primaryColor ?? defaultColors.primary.main;
  const secondaryColor = themeConfig?.secondaryColor ?? defaultColors.secondary.main;
  const isThemeOverriden = Boolean(themeConfig?.primaryColor);

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: isThemeOverriden ? { main: primaryColor } : defaultColors.primary,
          secondary: isThemeOverriden ? { main: secondaryColor } : defaultColors.secondary,
          background: lightColors.background,
          text: lightColors.text,
          divider: lightColors.divider,
          ...lightSemanticColors,
        },
        typography: baseTypography,
        shape: baseShape,
        shadows: baseShadows,
        components: lightModeComponents,
      }),
    [primaryColor, secondaryColor, isThemeOverriden]
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

export default ThemeWrapper;
